'use client';

/**
 * Offline queue for inventory movements (quick-issue, quick-receive,
 * quick-return, issue-to-job) and stock count actions (create, record,
 * complete). When the device is offline, SmartTask mobile modals queue
 * operations here instead of POSTing to the API. masterSync flushes the
 * queue when connectivity is restored.
 *
 * Storage uses the same appShellStore pattern as smartTaskOfflineEntries,
 * smartTaskOfflineQuoteRequests, etc. — one JSON array per queue type,
 * keyed by client_id.
 */
import appShellStore from '@/lib/offline/appShellStore';
import { getApiUrl } from '@/lib/apiConfig';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';

// ── Movement queue ────────────────────────────────────────────────────────

export interface PendingInventoryMovement {
  id: string;
  /** API endpoint path (e.g. /api/v1/work-items/inventory/quick-issue) */
  endpoint: string;
  /** HTTP method (POST or PUT) */
  method: 'POST' | 'PUT';
  /** JSON body to send */
  payload: Record<string, unknown>;
  /** Human-readable label for the sync progress UI */
  label: string;
  createdAt: string;
  retryCount: number;
  lastError?: string;
}

const MOVEMENT_QUEUE_KEY = 'smarttask_pending_inventory_movements';
const MAX_MOVEMENT_RETRIES = 5;

export async function loadPendingInventoryMovements(clientId: number): Promise<PendingInventoryMovement[]> {
  return appShellStore.loadSettingsArrayMigration<PendingInventoryMovement>(clientId, MOVEMENT_QUEUE_KEY);
}

export async function savePendingInventoryMovements(clientId: number, items: PendingInventoryMovement[]): Promise<void> {
  await appShellStore.saveSettingsArrayMigration(clientId, MOVEMENT_QUEUE_KEY, items);
}

export async function getPendingInventoryMovementCount(clientId: number): Promise<number> {
  if (!clientId) return 0;
  const items = await loadPendingInventoryMovements(clientId);
  return items.length;
}

export async function queueInventoryMovement(
  clientId: number,
  entry: Omit<PendingInventoryMovement, 'id' | 'createdAt' | 'retryCount'>,
): Promise<void> {
  const items = await loadPendingInventoryMovements(clientId);
  const id = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? `inv_${crypto.randomUUID()}`
    : `inv_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  items.push({
    ...entry,
    id,
    createdAt: new Date().toISOString(),
    retryCount: 0,
  });
  await savePendingInventoryMovements(clientId, items);
}

export async function syncPendingInventoryMovements(params: {
  clientId: number;
  token: string;
  onProgress?: (progress: { current: number; total: number; message: string }) => void;
}): Promise<{ synced: number; failed: number; remaining: number }> {
  const { clientId, token, onProgress } = params;
  if (!clientId || !token) {
    return { synced: 0, failed: 0, remaining: 0 };
  }

  const queue = await loadPendingInventoryMovements(clientId);
  if (queue.length === 0) {
    return { synced: 0, failed: 0, remaining: 0 };
  }

  const remaining: PendingInventoryMovement[] = [];
  let synced = 0;
  let failed = 0;

  for (let index = 0; index < queue.length; index += 1) {
    const entry = queue[index];
    onProgress?.({
      current: index + 1,
      total: queue.length,
      message: `Syncing inventory movement ${index + 1} of ${queue.length}: ${entry.label}`,
    });

    try {
      const res = await fetch(getApiUrl(entry.endpoint), {
        method: entry.method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(entry.payload),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message = body?.error || 'Failed to sync inventory movement';
        if (res.status >= 400 && res.status < 500) {
          throw new Error(`NON_RETRYABLE:${message}`);
        }
        throw new Error(message);
      }

      synced += 1;
    } catch (error: unknown) {
      failed += 1;
      const errMsg = error instanceof Error ? error.message : String(error);
      const nonRetryable = typeof errMsg === 'string' && errMsg.startsWith('NON_RETRYABLE:');
      const nextRetryCount = entry.retryCount + 1;
      if (!nonRetryable && nextRetryCount < MAX_MOVEMENT_RETRIES) {
        remaining.push({
          ...entry,
          retryCount: nextRetryCount,
          lastError: errMsg || 'Sync failed',
        });
      } else {
        if (!isSilentFetchError(error)) {
          console.error('[InventoryOfflineQueue] Dropping failed movement:', errMsg || error);
        }
      }
    }
  }

  await savePendingInventoryMovements(clientId, remaining);
  return { synced, failed, remaining: remaining.length };
}

// ── Stock count queue ─────────────────────────────────────────────────────
// Stock counts have multiple API calls per count (create → start → record →
// complete). We queue each as a separate operation and replay in order.

export interface PendingStockCountAction {
  id: string;
  /** Full API endpoint path (includes countId for PATCH/PUT) */
  endpoint: string;
  /** HTTP method */
  method: 'POST' | 'PUT' | 'PATCH';
  /** JSON body to send */
  payload: Record<string, unknown>;
  /** Human-readable label */
  label: string;
  createdAt: string;
  retryCount: number;
  lastError?: string;
}

const STOCK_COUNT_QUEUE_KEY = 'smarttask_pending_stock_count_actions';
const MAX_STOCK_COUNT_RETRIES = 5;

export async function loadPendingStockCountActions(clientId: number): Promise<PendingStockCountAction[]> {
  return appShellStore.loadSettingsArrayMigration<PendingStockCountAction>(clientId, STOCK_COUNT_QUEUE_KEY);
}

export async function savePendingStockCountActions(clientId: number, items: PendingStockCountAction[]): Promise<void> {
  await appShellStore.saveSettingsArrayMigration(clientId, STOCK_COUNT_QUEUE_KEY, items);
}

export async function getPendingStockCountActionCount(clientId: number): Promise<number> {
  if (!clientId) return 0;
  const items = await loadPendingStockCountActions(clientId);
  return items.length;
}

export async function queueStockCountAction(
  clientId: number,
  entry: Omit<PendingStockCountAction, 'id' | 'createdAt' | 'retryCount'>,
): Promise<void> {
  const items = await loadPendingStockCountActions(clientId);
  const id = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? `sc_${crypto.randomUUID()}`
    : `sc_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  items.push({
    ...entry,
    id,
    createdAt: new Date().toISOString(),
    retryCount: 0,
  });
  await savePendingStockCountActions(clientId, items);
}

export async function syncPendingStockCountActions(params: {
  clientId: number;
  token: string;
  onProgress?: (progress: { current: number; total: number; message: string }) => void;
}): Promise<{ synced: number; failed: number; remaining: number }> {
  const { clientId, token, onProgress } = params;
  if (!clientId || !token) {
    return { synced: 0, failed: 0, remaining: 0 };
  }

  const queue = await loadPendingStockCountActions(clientId);
  if (queue.length === 0) {
    return { synced: 0, failed: 0, remaining: 0 };
  }

  const remaining: PendingStockCountAction[] = [];
  let synced = 0;
  let failed = 0;

  for (let index = 0; index < queue.length; index += 1) {
    const entry = queue[index];
    onProgress?.({
      current: index + 1,
      total: queue.length,
      message: `Syncing stock count action ${index + 1} of ${queue.length}: ${entry.label}`,
    });

    try {
      const res = await fetch(getApiUrl(entry.endpoint), {
        method: entry.method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(entry.payload),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message = body?.error || 'Failed to sync stock count action';
        if (res.status >= 400 && res.status < 500) {
          throw new Error(`NON_RETRYABLE:${message}`);
        }
        throw new Error(message);
      }

      synced += 1;
    } catch (error: unknown) {
      failed += 1;
      const errMsg = error instanceof Error ? error.message : String(error);
      const nonRetryable = typeof errMsg === 'string' && errMsg.startsWith('NON_RETRYABLE:');
      const nextRetryCount = entry.retryCount + 1;
      if (!nonRetryable && nextRetryCount < MAX_STOCK_COUNT_RETRIES) {
        remaining.push({
          ...entry,
          retryCount: nextRetryCount,
          lastError: errMsg || 'Sync failed',
        });
      } else {
        if (!isSilentFetchError(error)) {
          console.error('[InventoryOfflineQueue] Dropping failed stock count action:', errMsg || error);
        }
      }
    }
  }

  await savePendingStockCountActions(clientId, remaining);
  return { synced, failed, remaining: remaining.length };
}
